import { useId, type CSSProperties, type ReactNode } from "react";
// The brand book keeps a copy (app/src/studio/brand/IsoMarket.tsx); keep the two in step.
const CHART_COLORS = { primary: "#003e56" };
const SMOOTH_EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";

// Brand graphics: an isometric market of self-storage facilities. Straight lines only, navy for
// ours and white and slate for everyone else. Motion is CSS keyframes on a 6 s cycle, so a capture
// can pause every animation and step through it (document.getAnimations()).

const NAVY = CHART_COLORS.primary;

// One grid unit on screen, and the 30° isometric projection.
const S = 16;
const COS30 = Math.cos(Math.PI / 6);
const GRID = 20;
const FLOOR_H = 0.9;
// Walls reach this far up under whatever sits on them (the next floor, the roof). Two shapes that only
// meet edge to edge leave a hairline of background between them where both edges are smoothed.
const SEAM = 0.04;
const ROW_H = 1;
const DOOR_H = 0.64;
// The part of a door that opens, below its housing.
const DOOR_OPENING = DOOR_H * 0.84;

type Point = [number, number];

/** Plane (x, y) at height z, to screen. */
export function iso(x: number, y: number, z = 0): Point {
  return [(x - y) * S * COS30, (x + y) * S * 0.5 - z * S];
}

export function pts(points: Point[]) {
  return points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
}

function Line({ a, b, stroke, width }: { a: Point; b: Point; stroke: string; width: number }) {
  return <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={stroke} strokeWidth={width} strokeLinecap="square" />;
}

// The scene's entrance, in ms. Buildings drop in one after another (a tower floor by floor) and
// their shadows fade in under them; the price flags drop as the last building lands; the loops
// (doors, price swaps, the ring's pulse) start once it has all settled. entranceTimeline() works
// out the last two from the buildings, so a scene with more of them takes longer.
export const ENTRANCE = {
  /** The first building. */
  first: 150,
  /** Between one building and the next. */
  stagger: 80,
  /** Between a tower's floors. */
  floor: 100,
  /** Each drop. */
  drop: 460,
  /** Each fade: shadows, the market's glow. */
  fade: 500,
  /** Between one price flag and the next. */
  flagStagger: 100,
};

// The trucks' path: along the north-south road, off the plane at both ends.
const DRIVE_FROM = -3;
const DRIVE_TO = GRID + 2;

export function BrandMotionStyles() {
  const along = (dy: number) => `${(-(dy * S * COS30)).toFixed(1)}px, ${(dy * S * 0.5).toFixed(1)}px`;
  return (
    <style>{`
      @keyframes bb-drop { from { opacity: 0; transform: translateY(-22px); } to { opacity: 1; transform: none; } }
      .bb-drop { animation: bb-drop ${ENTRANCE.drop}ms ${SMOOTH_EASE} both; }
      @keyframes bb-fade { from { opacity: 0; } to { opacity: 1; } }
      .bb-fade { animation: bb-fade ${ENTRANCE.fade}ms ${SMOOTH_EASE} both; }
      @keyframes bb-trace { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
      .bb-trace { stroke-dasharray: 100; stroke-dashoffset: 100; animation: bb-trace 800ms ${SMOOTH_EASE} ${ENTRANCE.first}ms forwards; }
      @keyframes bb-pulse { 0% { opacity: 0.5; transform: scale(1); } 75%, 100% { opacity: 0; transform: scale(1.9); } }
      .bb-pulse { transform-box: view-box; animation: bb-pulse 3s ${SMOOTH_EASE} var(--bb-loops, 2000ms) infinite both; }
      @keyframes bb-roll { 0%, 100% { transform: translateY(0); } 15%, 45% { transform: translateY(${(-DOOR_OPENING * S).toFixed(2)}px); } 60% { transform: translateY(0); } }
      .bb-roll { animation: bb-roll 6s cubic-bezier(0.45, 0, 0.25, 1) infinite both; }
      @keyframes bb-swap-a { 0%, 44% { opacity: 1; } 50%, 94% { opacity: 0; } 100% { opacity: 1; } }
      @keyframes bb-swap-b { 0%, 44% { opacity: 0; } 50%, 94% { opacity: 1; } 100% { opacity: 0; } }
      .bb-swap-a { animation: bb-swap-a 6s linear var(--bb-loops, 2000ms) infinite both; }
      .bb-swap-b { animation: bb-swap-b 6s linear var(--bb-loops, 2000ms) infinite both; }
      @media (prefers-reduced-motion) { .bb-swap-b { opacity: 0; } }
      @keyframes bb-drive-south { 0% { opacity: 0; transform: translate(0, 0); } 6%, 94% { opacity: 1; } 100% { opacity: 0; transform: translate(${along(DRIVE_TO - DRIVE_FROM)}); } }
      @keyframes bb-drive-north { 0% { opacity: 0; transform: translate(0, 0); } 6%, 94% { opacity: 1; } 100% { opacity: 0; transform: translate(${along(-(DRIVE_TO - DRIVE_FROM))}); } }
      .bb-drive-south { animation: bb-drive-south 6s linear infinite both; }
      .bb-drive-north { animation: bb-drive-north 6s linear 3000ms infinite both; }
    `}</style>
  );
}

// ---------------------------------------------------------------------------
// Buildings

interface Palette {
  top: string;
  left: string;
  right: string;
  edge: string;
  detail: string;
  door: string;
  doorLine: string;
  /** The roll housing across the top of a door, and the unit's inside when it's open. */
  housing: string;
  interior: string;
  glass: string;
  window: string;
  shadow: string;
}

const PALETTE: Record<"ours" | "theirs", Palette> = {
  ours: {
    top: "#2f6a84",
    left: "#0e4c66",
    right: NAVY,
    edge: "#00283a",
    detail: "rgba(255,255,255,0.22)",
    door: "#c9d8e0",
    doorLine: "#9cb6c3",
    housing: "#7f9eae",
    interior: "#00202e",
    glass: "#6b9db5",
    window: "rgba(255,255,255,0.1)",
    shadow: "rgba(0,40,58,0.12)",
  },
  theirs: {
    top: "#ffffff",
    left: "#f3f5f8",
    right: "#e2e7ed",
    edge: "#8796a9",
    detail: "#c6cfd9",
    door: "#d3dce4",
    housing: "#bcc8d2",
    interior: "#51677a",
    doorLine: "#b3bfcc",
    glass: "#cdd8e1",
    window: "#e3e9ee",
    shadow: "rgba(15,23,42,0.07)",
  },
};

export interface Building {
  x: number;
  y: number;
  w: number;
  d: number;
  ours: boolean;
  /** A drive-up row: one storey of roll-up doors along the face named. */
  doors?: "left" | "right";
  /** A multi-storey building: its floors. */
  floors?: number;
  /** Door indexes that roll open now and then. */
  opens?: number[];
}

/** The two visible walls: left runs along x at y + d, right runs along y at x + w. */
function faces(b: Building) {
  return {
    left: (t: number, z: number) => iso(b.x + t, b.y + b.d, z),
    right: (t: number, z: number) => iso(b.x + b.w, b.y + t, z),
  };
}

function wallQuad(at: (t: number, z: number) => Point, t0: number, t1: number, z0: number, z1: number) {
  return pts([at(t0, z0), at(t1, z0), at(t1, z1), at(t0, z1)]);
}

/** A roll-up door under its housing. One that opens rolls its panel up into the housing and back
 *  down: the panel slides straight up the wall (walls are vertical on screen, so a plain translate
 *  keeps its slanted bottom edge exact), clipped to the opening, uncovering the dark inside from the
 *  floor up. */
function RollUpDoor({ at, t0, t1, p, open, delay }: {
  at: (t: number, z: number) => Point;
  t0: number;
  t1: number;
  p: Palette;
  open: boolean;
  delay: number;
}) {
  const clip = useId();
  return (
    <g>
      <polygon points={wallQuad(at, t0, t1, 0, DOOR_H)} fill={p.door} />
      {open && (
        <>
          <clipPath id={clip}>
            <polygon points={wallQuad(at, t0, t1, 0, DOOR_OPENING)} />
          </clipPath>
          <polygon points={wallQuad(at, t0, t1, 0, DOOR_OPENING)} fill={p.interior} />
          <g clipPath={`url(#${clip})`}>
            <polygon className="bb-roll" style={{ animationDelay: `calc(var(--bb-loops, 2000ms) + ${delay}ms)` }} points={wallQuad(at, t0, t1, 0, DOOR_OPENING)} fill={p.door} />
          </g>
        </>
      )}
      <polygon points={wallQuad(at, t0, t1, DOOR_H * 0.84, DOOR_H)} fill={p.housing} />
    </g>
  );
}

/** A box's three faces with an outline, for small parts (a rooftop unit, a truck). */
export function Box({ x, y, z, w, d, h, top, left, right, edge }: {
  x: number; y: number; z: number; w: number; d: number; h: number;
  top: string; left: string; right: string; edge: string;
}) {
  return (
    <g strokeWidth={0.6} stroke={edge} strokeLinejoin="miter">
      <polygon points={pts([iso(x, y + d, z), iso(x + w, y + d, z), iso(x + w, y + d, z + h), iso(x, y + d, z + h)])} fill={left} />
      <polygon points={pts([iso(x + w, y, z), iso(x + w, y + d, z), iso(x + w, y + d, z + h), iso(x + w, y, z + h)])} fill={right} />
      <polygon points={pts([iso(x, y, z + h), iso(x + w, y, z + h), iso(x + w, y + d, z + h), iso(x, y + d, z + h)])} fill={top} />
    </g>
  );
}

function Roof({ b, h, p }: { b: Building; h: number; p: Palette }) {
  const { x, y, w, d } = b;
  const inset = 0.14;
  return (
    <>
      <polygon points={pts([iso(x, y, h), iso(x + w, y, h), iso(x + w, y + d, h), iso(x, y + d, h)])} fill={p.top} stroke={p.edge} strokeWidth={0.9} strokeLinejoin="miter" />
      <polygon points={pts([iso(x + inset, y + inset, h), iso(x + w - inset, y + inset, h), iso(x + w - inset, y + d - inset, h), iso(x + inset, y + d - inset, h)])} fill="none" stroke={p.detail} strokeWidth={0.55} />
    </>
  );
}

/** A single-storey drive-up row: roll-up doors at an even pitch along one wall. */
export function DriveUpRow({ b, delay }: { b: Building; delay: number }) {
  const p = b.ours ? PALETTE.ours : PALETTE.theirs;
  const f = faces(b);
  const along = b.doors === "right" ? b.d : b.w;
  const end = b.doors === "right" ? b.w : b.d;
  const count = Math.round(along / 1.1);
  const pitch = along / count;
  const doorWall = b.doors === "right" ? f.right : f.left;
  const plainWall = b.doors === "right" ? f.left : f.right;
  return (
    <g className="bb-drop" style={{ animationDelay: `${delay}ms` }}>
      <polygon points={wallQuad(f.left, 0, b.w, 0, ROW_H + SEAM)} fill={p.left} stroke={p.edge} strokeWidth={0.9} strokeLinejoin="miter" />
      <polygon points={wallQuad(f.right, 0, b.d, 0, ROW_H + SEAM)} fill={p.right} stroke={p.edge} strokeWidth={0.9} strokeLinejoin="miter" />
      <Line a={doorWall(0, DOOR_H + 0.1)} b={doorWall(along, DOOR_H + 0.1)} stroke={p.detail} width={0.55} />
      {Array.from({ length: count }, (_, i) => (
        <RollUpDoor
          key={i}
          at={doorWall}
          t0={i * pitch + pitch * 0.12}
          t1={(i + 1) * pitch - pitch * 0.12}
          p={p}
          open={b.opens?.includes(i) ?? false}
          delay={((i * 7 + Math.round(b.x * 3 + b.y * 5)) % 6) * 900}
        />
      ))}
      {/* A side door on the plain end. */}
      <polygon points={wallQuad(plainWall, end / 2 - 0.18, end / 2 + 0.18, 0, 0.58)} fill={p.door} stroke={p.doorLine} strokeWidth={0.55} />
      <Roof b={b} h={ROW_H} p={p} />
    </g>
  );
}

/** A multi-storey building, dropped in floor by floor: a glazed entrance and a loading door on the
 *  ground floor, a band at each floor line, ribbon windows above, then the roof. */
export function Tower({ b, delay }: { b: Building; delay: number }) {
  const p = b.ours ? PALETTE.ours : PALETTE.theirs;
  const f = faces(b);
  const floors = b.floors ?? 2;
  const top = floors * FLOOR_H;
  return (
    <g>
      {Array.from({ length: floors }, (_, level) => {
        const z0 = level * FLOOR_H;
        const z1 = z0 + FLOOR_H;
        const floorLine = level === 0 ? p.edge : p.detail;
        const floorWidth = level === 0 ? 0.9 : 1.1;
        return (
          <g key={level} className="bb-drop" style={{ animationDelay: `${delay + level * ENTRANCE.floor}ms` }}>
            <polygon points={wallQuad(f.left, 0, b.w, z0, z1 + SEAM)} fill={p.left} />
            <polygon points={wallQuad(f.right, 0, b.d, z0, z1 + SEAM)} fill={p.right} />
            {/* Corners and the floor line; the floor above draws its own. */}
            <Line a={f.left(0, z0)} b={f.left(0, z1)} stroke={p.edge} width={0.9} />
            <Line a={f.left(b.w, z0)} b={f.left(b.w, z1)} stroke={p.edge} width={0.9} />
            <Line a={f.right(0, z0)} b={f.right(0, z1)} stroke={p.edge} width={0.9} />
            <Line a={f.left(0, z0)} b={f.left(b.w, z0)} stroke={floorLine} width={floorWidth} />
            <Line a={f.right(0, z0)} b={f.right(b.d, z0)} stroke={floorLine} width={floorWidth} />
            {level === 0 ? (
              <>
                {/* Entrance: glass with mullions under a canopy line. */}
                <polygon points={wallQuad(f.left, b.w - 1.5, b.w - 0.4, 0, 0.62)} fill={p.glass} stroke={p.edge} strokeWidth={0.55} />
                {[1, 2].map((m) => {
                  const t = b.w - 1.5 + (1.1 * m) / 3;
                  return <Line key={m} a={f.left(t, 0)} b={f.left(t, 0.62)} stroke={p.edge} width={0.45} />;
                })}
                <Line a={f.left(b.w - 1.65, 0.72)} b={f.left(b.w - 0.25, 0.72)} stroke={p.edge} width={1.2} />
                <RollUpDoor at={f.right} t0={b.d / 2 - 0.45} t1={b.d / 2 + 0.45} p={p} open={b.opens?.includes(0) ?? false} delay={600} />
              </>
            ) : (
              <>
                {/* A ribbon window along each face. */}
                <polygon points={wallQuad(f.left, 0.3, b.w - 0.3, z0 + 0.24, z0 + 0.68)} fill={p.window} />
                <polygon points={wallQuad(f.right, 0.3, b.d - 0.3, z0 + 0.24, z0 + 0.68)} fill={p.window} />
              </>
            )}
          </g>
        );
      })}
      <g className="bb-drop" style={{ animationDelay: `${delay + floors * ENTRANCE.floor}ms` }}>
        <Roof b={b} h={top} p={p} />
      </g>
    </g>
  );
}

export function Shadow({ b }: { b: Building }) {
  const h = b.floors ? b.floors * FLOOR_H : ROW_H;
  const s = h * 0.9;
  const { x, y, w, d } = b;
  const p = b.ours ? PALETTE.ours : PALETTE.theirs;
  return <polygon points={pts([iso(x + w, y), iso(x + w + s, y + s * 0.3), iso(x + w + s, y + d + s * 0.3), iso(x + w, y + d)])} fill={p.shadow} />;
}

// ---------------------------------------------------------------------------
// The market

type Rect = [number, number, number, number];
type PlanePoint = [number, number];

export interface Facility {
  /** The lot: a paved pad under the buildings, as x0, y0, x1, y1. */
  lot: Rect;
  ours: boolean;
  buildings: Building[];
  /** The price flag sits over this building. */
  flagOn: number;
  price: string | [string, string];
}

export const row = (x: number, y: number, w: number, d: number, ours: boolean, doors: "left" | "right", opens?: number[]): Building => ({ x, y, w, d, ours, doors, opens });
export const tower = (x: number, y: number, w: number, d: number, ours: boolean, floors: number, opens?: number[]): Building => ({ x, y, w, d, ours, floors, opens });

// Four square blocks around a crossroads: north-south road at x 9 to 10.4, east-west at y 9 to 10.4.
const ROAD = { from: 9, to: 10.4, centre: 9.7 };

// Our block's edge: a square inside the curbs, its corners cut to make a regular octagon.
const MARKET: Rect = [0.4, 0.4, 8.8, 8.8];

const OURS: Facility = {
  lot: MARKET,
  ours: true,
  // Low rows at the back and the tall building at the front, so nothing hides the octagon's far side.
  buildings: [
    row(2.4, 1.6, 4.6, 1.1, true, "left", [2]),
    row(2, 3.2, 1.1, 4.2, true, "right", [1]),
    tower(4.4, 4.4, 2.8, 2.8, true, 3, [0]),
  ],
  flagOn: 2,
  price: ["$141", "$152"],
};
const WEST: Facility = {
  lot: [0.6, 11, 8.4, 19.4],
  ours: false,
  buildings: [row(1.2, 11.6, 6.6, 1.3, false, "left", [3]), row(1.2, 13.9, 6.6, 1.3, false, "left"), row(1.2, 16.2, 6.6, 1.3, false, "left", [1]), row(1.2, 18.3, 6.6, 1, false, "left")],
  flagOn: 1,
  price: "$136",
};
const EAST: Facility = {
  lot: [11, 0.6, 19.4, 8.4],
  ours: false,
  buildings: [tower(14.8, 1.2, 3.6, 3, false, 2), row(11.6, 5, 6.8, 1.3, false, "left", [4]), row(11.6, 7, 6.8, 1.2, false, "left")],
  flagOn: 0,
  price: "$158",
};
const SOUTHEAST: Facility = {
  lot: [11, 11, 19.4, 19.4],
  ours: false,
  buildings: [tower(12, 11.6, 4.2, 3.4, false, 4, [0]), row(17.4, 11.6, 1.3, 5.4, false, "right", [2])],
  flagOn: 0,
  price: "$162",
};
const RIVALS = [WEST, EAST, SOUTHEAST];

const byDepth = (a: Building, b: Building) => a.x + a.w + a.y + a.d - (b.x + b.w + b.y + b.d);

/** A rate over a building: on a pole with a bordered flag, or (soft) a borderless tag with a small
 *  pointer and a gentle shadow, for the landscape, which draws no lines. */
function PriceFlag({ b, price, delay, soft = false }: { b: Building; price: string | [string, string]; delay: number; soft?: boolean }) {
  const top = b.floors ? b.floors * FLOOR_H : ROW_H;
  const [fx, fy] = iso(b.x + b.w / 2, b.y + b.d / 2, top);
  const flagY = soft ? fy - 26 : fy - 30;
  const prices = typeof price === "string" ? [price] : price;
  const fill = b.ours ? NAVY : "#ffffff";
  return (
    <g className="bb-drop" data-kind="flag" style={{ animationDelay: `${delay}ms`, filter: soft ? "drop-shadow(0 1.5px 2.5px rgba(15,23,42,0.16))" : undefined }}>
      {soft ? (
        <>
          <rect x={fx - 20} y={flagY} width={40} height={16} fill={fill} />
          <polygon points={`${fx - 4},${flagY + 16} ${fx + 4},${flagY + 16} ${fx},${flagY + 20.5}`} fill={fill} />
        </>
      ) : (
        <>
          <line x1={fx} y1={fy} x2={fx} y2={flagY + 16} stroke={b.ours ? NAVY : "#8796a9"} strokeWidth={0.9} />
          <rect x={fx - 20} y={flagY} width={40} height={16} fill={fill} stroke={b.ours ? NAVY : "#8796a9"} strokeWidth={0.9} />
        </>
      )}
      {prices.map((value, i) => (
        <text
          key={value}
          className={prices.length > 1 ? (i === 0 ? "bb-swap-a" : "bb-swap-b") : undefined}
          x={fx}
          y={flagY + 11.5}
          textAnchor="middle"
          fontSize={9.5}
          fontWeight={600}
          fill={b.ours ? "#ffffff" : "#334155"}
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {value}
        </text>
      ))}
    </g>
  );
}

/** An octagon with its sides on the grid: a rectangle with its corners cut at 45°. */
function gridOctagon([x0, y0, x1, y1]: Rect, cut: number): PlanePoint[] {
  return [
    [x0 + cut, y0], [x1 - cut, y0], [x1, y0 + cut], [x1, y1 - cut],
    [x1 - cut, y1], [x0 + cut, y1], [x0, y1 - cut], [x0, y0 + cut],
  ];
}

const project = (points: PlanePoint[], z = 0) => points.map(([x, y]) => iso(x, y, z));

/** The polygon's edges, split into those behind the buildings inside it and those in front. */
function edges(points: PlanePoint[], side: "back" | "front") {
  return points
    .map((a, i) => [a, points[(i + 1) % points.length]!] as const)
    .filter(([a, b]) => {
      // Clockwise on the plane, so the outward normal of a→b is (dy, -dx). Facing the viewer = +x +y.
      const facing = b[1] - a[1] - (b[0] - a[0]);
      return side === "front" ? facing > 0.001 : facing <= 0.001;
    });
}

function Fence({ outline, side }: { outline: PlanePoint[]; side: "back" | "front" }) {
  const h = 0.32;
  return (
    <g>
      {edges(outline, side).map(([[ax, ay], [bx, by]]) => {
        const posts = Math.max(1, Math.round(Math.hypot(bx - ax, by - ay) / 0.8));
        return (
          <g key={`${ax},${ay}`}>
            <Line a={iso(ax, ay, h)} b={iso(bx, by, h)} stroke="#8796a9" width={0.6} />
            <Line a={iso(ax, ay, h * 0.45)} b={iso(bx, by, h * 0.45)} stroke="#b3bfcc" width={0.45} />
            {Array.from({ length: posts + 1 }, (_, i) => {
              const t = i / posts;
              return <Line key={i} a={iso(ax + (bx - ax) * t, ay + (by - ay) * t, 0)} b={iso(ax + (bx - ax) * t, ay + (by - ay) * t, h)} stroke="#8796a9" width={0.5} />;
            })}
          </g>
        );
      })}
    </g>
  );
}

/** A box truck driving along y: a white cargo box with a navy stripe, a lower navy cab ahead of
 *  it with a windshield, and wheels. */
export const TRUCKS = [
  { lane: 9.12, heading: "south" },
  { lane: 9.82, heading: "north" },
] as const;

type Heading = "south" | "north";

/** The screen box a truck covers where it starts, with a little room for its edges. */
export function truckBox(lane: number, heading: Heading) {
  const y = heading === "south" ? DRIVE_FROM : DRIVE_TO;
  const corners = [lane - 0.05, lane + 0.6].flatMap((x) => [y - 0.1, y + 1.8].flatMap((yy) => [0, 0.8].map((z) => iso(x, yy, z))));
  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  const [x, top] = [Math.min(...xs) - 2, Math.min(...ys) - 2];
  return { x, y: top, w: Math.max(...xs) + 2 - x, h: Math.max(...ys) + 2 - top };
}

/** The class that drives a truck along its lane (works on SVG groups and HTML layers alike). */
export const driveClass = (heading: Heading) => (heading === "south" ? "bb-drive-south" : "bb-drive-north");

function Truck({ lane, heading }: { lane: number; heading: Heading }) {
  return (
    <g className={driveClass(heading)}>
      <TruckBody lane={lane} heading={heading} />
    </g>
  );
}

/** A box truck at the start of its lane, still. */
export function TruckBody({ lane, heading }: { lane: number; heading: Heading }) {
  const w = 0.5;
  const cargo = { d: 1.2, h: 0.62 };
  const cab = { d: 0.42, h: 0.44 };
  const south = heading === "south";
  // South-bound trucks face +y (towards the viewer's left), so the cab is at the larger y.
  const y = south ? DRIVE_FROM : DRIVE_TO;
  const cargoY = south ? y : y + cab.d + 0.04;
  const cabY = south ? y + cargo.d + 0.04 : y;
  const x1 = lane + w;
  const wheel = (t0: number) => (
    <polygon points={pts([iso(x1 + 0.01, t0, 0), iso(x1 + 0.01, t0 + 0.24, 0), iso(x1 + 0.01, t0 + 0.24, 0.14), iso(x1 + 0.01, t0, 0.14)])} fill="#334155" />
  );
  // The nearer part is drawn last: the cab for a south-bound truck, the cargo box for a north-bound one.
  const cabBox = (
    <>
      <Box x={lane} y={cabY} z={0.08} w={w} d={cab.d} h={cab.h} top="#2f6a84" left="#0e4c66" right={NAVY} edge="#00283a" />
      {south && (
        <polygon points={pts([iso(lane + 0.06, cabY + cab.d, 0.32), iso(x1 - 0.06, cabY + cab.d, 0.32), iso(x1 - 0.06, cabY + cab.d, 0.46), iso(lane + 0.06, cabY + cab.d, 0.46)])} fill="#9cb6c3" />
      )}
    </>
  );
  return (
    <g>
      {!south && cabBox}
      <Box x={lane} y={cargoY} z={0.08} w={w} d={cargo.d} h={cargo.h} top="#ffffff" left="#f3f5f8" right="#e2e7ed" edge="#64748b" />
      <polygon points={pts([iso(x1, cargoY + 0.1, 0.42), iso(x1, cargoY + cargo.d - 0.1, 0.42), iso(x1, cargoY + cargo.d - 0.1, 0.5), iso(x1, cargoY + 0.1, 0.5)])} fill={NAVY} />
      {south && cabBox}
      {wheel(cargoY + 0.12)}
      {wheel(cargoY + cargo.d - 0.36)}
      {wheel(cabY + cab.d / 2 - 0.12)}
    </g>
  );
}

function lotQuad([x0, y0, x1, y1]: Rect) {
  return pts([iso(x0, y0), iso(x1, y0), iso(x1, y1), iso(x0, y1)]);
}

// When each building starts to drop in: lot by lot in draw order, far to near within a lot.
function dropDelays(facilities: Facility[]) {
  return new Map<Building, number>(facilities.flatMap((f) => [...f.buildings].sort(byDepth)).map((b, i) => [b, ENTRANCE.first + i * ENTRANCE.stagger]));
}

/** A piece of the market drawn in its own small SVG: its box on screen (in the scene's units) and
 *  what's in it. */
export interface MarketPiece {
  key: string;
  box: { x: number; y: number; w: number; h: number };
  node: ReactNode;
}

/** The box around some plane rectangles standing up to a height, with room for strokes. */
function screenBox(parts: { x: number; y: number; w: number; d: number; top: number }[], pad = 3) {
  const corners = parts.flatMap(({ x, y, w, d, top }) => [x, x + w].flatMap((px) => [y, y + d].flatMap((py) => [0, top].map((z) => iso(px, py, z)))));
  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  const [x0, y0] = [Math.min(...xs) - pad, Math.min(...ys) - pad];
  return { x: x0, y: y0, w: Math.max(...xs) + pad - x0, h: Math.max(...ys) + pad - y0 };
}

/** The market for a landscape, as small pieces in drawing order: our lot and the lots west of the
 *  road (behind the trucks), the lots east of it (in front), and the price flags. Each lot is its own
 *  SVG, so a rolling door or a dropping building redraws only its lot: Safari redraws a whole SVG
 *  whenever anything in it moves, and the scene's full-screen SVGs made every door roll redraw the
 *  market. */
export function marketPieces(ours: Facility, rivals: Facility[]) {
  const facilities = [ours, ...rivals];
  const delays = dropDelays(facilities);
  const timeline = entranceTimeline(facilities);
  const lot = (f: Facility): MarketPiece => ({
    key: f.lot.join(),
    box: screenBox(f.buildings.map((b) => ({ ...b, top: (b.floors ? b.floors * FLOOR_H : ROW_H) + 0.2 }))),
    node: drawLot(f, delays),
  });
  const flags = facilities.map((f, i): MarketPiece => {
    const b = f.buildings[f.flagOn]!;
    const top = b.floors ? b.floors * FLOOR_H : ROW_H;
    const [fx, fy] = iso(b.x + b.w / 2, b.y + b.d / 2, top);
    return {
      key: `flag ${f.lot.join()}`,
      // The tag (40 by 16, its pointer below) and its soft shadow.
      box: { x: fx - 26, y: fy - 32, w: 52, h: 32 },
      node: <PriceFlag b={b} price={f.price} delay={timeline.flags + i * ENTRANCE.flagStagger} soft />,
    };
  });
  return {
    back: [ours, ...rivals.filter((f) => f.lot[2] <= ROAD.from)].map(lot),
    front: rivals.filter((f) => f.lot[2] > ROAD.from).map(lot),
    flags,
  };
}

/** When the last building has landed, when the price flags start (as it lands), and when the scene
 *  has settled and the loops start. */
export function entranceTimeline(facilities: Facility[]) {
  const delays = dropDelays(facilities);
  const landed = Math.max(...facilities.flatMap((f) => f.buildings).map((b) => (delays.get(b) ?? 0) + (b.floors ?? 0) * ENTRANCE.floor + ENTRANCE.drop));
  const flags = landed - ENTRANCE.drop / 2;
  const settled = flags + (facilities.length - 1) * ENTRANCE.flagStagger + ENTRANCE.drop;
  return { landed, flags, settled };
}

function drawLot(facility: Facility, delays: Map<Building, number>) {
  return [...facility.buildings].sort(byDepth).map((b) => {
    const delay = delays.get(b) ?? 0;
    return b.floors ? <Tower key={`${b.x},${b.y}`} b={b} delay={delay} /> : <DriveUpRow key={`${b.x},${b.y}`} b={b} delay={delay} />;
  });
}

export function IsoMarket({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="-290 -60 580 395" className={className} aria-hidden>
      <IsoMarketScene />
    </svg>
  );
}

/** In a landscape, the country around the market draws the ground and roads; the market brings its
 *  lots, our facility, the ring around it and the rivals inside it. */
export interface MarketInLandscape {
  ours: Facility;
  rivals: Facility[];
  /** How far the market reaches from our lot, in grid units: a soft glow on the ground. */
  reach: number;
  /** Which part to draw, each on its own layer: the buildings' shadows (blurred once and faded in
   *  as a whole, so nothing re-blurs while the buildings move), or the ground (the market's glow
   *  and the pads). The buildings and flags come from marketPieces, each in its own small SVG. */
  part: "shadows" | "ground";
  pad: string;
  ourPad: string;
}

/** The market's ground, buildings, trucks and flags, for an SVG of any extent. */
export function IsoMarketScene({ landscape }: { landscape?: MarketInLandscape }) {
  const ours = landscape?.ours ?? OURS;
  const rivals = landscape?.rivals ?? RIVALS;
  const facilities = [ours, ...rivals];
  // In a landscape: the buildings' shadows, or the ground (the market's glow, then the pads). The
  // buildings and flags come from marketPieces.
  if (landscape) {
    const pad = (lot: Rect) => pts(project(gridOctagon(lot, 0.7)));
    return landscape.part === "shadows" ? (
      <g filter="url(#ls-soft)">
        {facilities.flatMap((f) => f.buildings).map((b) => (
          <Shadow key={`${b.x},${b.y}`} b={b} />
        ))}
      </g>
    ) : (
      <g>
        <MarketReach ours={ours.lot} reach={landscape.reach} />
        {rivals.map((f) => (
          <polygon key={f.lot.join()} points={pad(f.lot)} fill={landscape.pad} />
        ))}
        <polygon points={pad(ours.lot)} fill={landscape.ourPad} />
      </g>
    );
  }
  const delays = dropDelays(facilities);
  const timeline = entranceTimeline(facilities);
  // A lot west of the north-south road is drawn before the trucks on it, the rest after.
  const westOfRoad = rivals.filter((f) => f.lot[2] <= ROAD.from);
  const eastOfRoad = rivals.filter((f) => f.lot[2] > ROAD.from);
  // Our lot is the market's octagon: a regular octagon square to the roads, its fence just inside.
  const ringRect = MARKET;
  const ring = gridOctagon(ringRect, (ringRect[2] - ringRect[0]) / (2 + Math.SQRT2));
  const fenceLine = inset(ring, 0.35);
  const [ox, oy] = iso((ringRect[0] + ringRect[2]) / 2, (ringRect[1] + ringRect[3]) / 2);
  const ringPoints = pts(project(ring));
  return (
    <g style={{ "--bb-loops": `${timeline.settled}ms` } as CSSProperties}>
      {/* Ground, lots, grid, roads. */}
      <polygon points={lotQuad([0, 0, GRID, GRID])} fill="#f4f6f8" />
      {rivals.map((f) => (
        <polygon key={f.lot.join()} points={lotQuad(f.lot)} fill="#eceff3" />
      ))}
      <polygon points={pts(project(ring))} fill="#e6ecef" />
      {Array.from({ length: GRID + 1 }, (_, i) => (
        <g key={i}>
          <Line a={iso(i, 0)} b={iso(i, GRID)} stroke="#e3e8ed" width={0.45} />
          <Line a={iso(0, i)} b={iso(GRID, i)} stroke="#e3e8ed" width={0.45} />
        </g>
      ))}
      <polygon points={lotQuad([ROAD.from, 0, ROAD.to, GRID])} fill="#ffffff" />
      <polygon points={lotQuad([0, ROAD.from, GRID, ROAD.to])} fill="#ffffff" />
      <g strokeDasharray="4 4">
        <Line a={iso(ROAD.centre, 0)} b={iso(ROAD.centre, GRID)} stroke="#cbd5e1" width={0.7} />
        <Line a={iso(0, ROAD.centre)} b={iso(GRID, ROAD.centre)} stroke="#cbd5e1" width={0.7} />
      </g>
      <polygon points={lotQuad([0, 0, GRID, GRID])} fill="none" stroke="#cbd5e1" strokeWidth={0.7} />

      {/* The market's radius. */}
      <polygon points={ringPoints} fill="rgba(0,62,86,0.045)" />
      <polygon className="bb-pulse" style={{ transformOrigin: `${ox}px ${oy}px` }} points={ringPoints} fill="none" stroke={NAVY} strokeWidth={1.3} />
      <polygon className="bb-trace" pathLength={100} points={ringPoints} fill="none" stroke={NAVY} strokeWidth={1.3} />

      {/* Shadows land with their buildings: a tower's once its roof is on. */}
      {facilities.flatMap((f) => f.buildings).map((b) => (
        <g key={`${b.x},${b.y}`} className="bb-fade" style={{ animationDelay: `${(delays.get(b) ?? 0) + (b.floors ?? 0) * ENTRANCE.floor + 100}ms` }}>
          <Shadow b={b} />
        </g>
      ))}

      {/* Far to near: our lot (inside its fence, on its own), the lot south of it, the trucks on the
          road, then the lots east of the road, then the flags. */}
      <Fence outline={fenceLine} side="back" />
      {drawLot(ours, delays)}
      <Fence outline={fenceLine} side="front" />
      {westOfRoad.map((f) => drawLot(f, delays))}
      {TRUCKS.map((t) => <Truck key={t.lane} lane={t.lane} heading={t.heading} />)}
      {eastOfRoad.map((f) => drawLot(f, delays))}
      {facilities.map((f, i) => (
        <PriceFlag key={f.lot.join()} b={f.buildings[f.flagOn]!} price={f.price} delay={timeline.flags + i * ENTRANCE.flagStagger} />
      ))}
    </g>
  );
}

/** A circle on the ground around our lot: a glow that fades out to the market's edge. A circle on the
 *  plane is an ellipse on screen, √2 wider than tall. */
function MarketReach({ ours, reach }: { ours: Rect; reach: number }) {
  const [cx, cy] = iso((ours[0] + ours[2]) / 2, (ours[1] + ours[3]) / 2);
  const rx = reach * S * COS30 * Math.SQRT2;
  const ry = reach * S * 0.5 * Math.SQRT2;
  return (
    <>
      <defs>
        <radialGradient id="market-reach">
          <stop offset="0" stopColor={NAVY} stopOpacity="0.12" />
          <stop offset="0.7" stopColor={NAVY} stopOpacity="0.05" />
          <stop offset="1" stopColor={NAVY} stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse className="bb-fade" style={{ animationDelay: `${ENTRANCE.first + 350}ms` }} cx={cx} cy={cy} rx={rx} ry={ry} fill="url(#market-reach)" />
    </>
  );
}

/** A grid octagon pulled in by `by` on every side, for a fence just inside its edge. */
function inset(points: PlanePoint[], by: number): PlanePoint[] {
  const [x0, x1] = [Math.min(...points.map((p) => p[0])), Math.max(...points.map((p) => p[0]))];
  const [y0, y1] = [Math.min(...points.map((p) => p[1])), Math.max(...points.map((p) => p[1]))];
  return gridOctagon([x0 + by, y0 + by, x1 - by, y1 - by], (x1 - x0 - 2 * by) / (2 + Math.SQRT2));
}
